import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { LocalStorage } from 'ngx-webstorage';
import { environment } from 'src/environments/environment';

@Injectable()
export class GlobalErrorHandlerService implements ErrorHandler {

  @LocalStorage()
  private userDetails:any;
  @LocalStorage()
  private CustomerId:string;
  userData: any;
  customerId: any;
  constructor(private ngZone: NgZone, private router: Router) {}

  private getUser(): {
    userId: string;
    userName: string;
    email?: string;
    userGroup?: string;
  } {
    const userData = this.userDetails;

    return {
      userId: userData?.userID ?? 'Unknown',
      userName: userData?.name ?? 'Anonymous',
      userGroup: userData?.userGroup?.[0]?.Code ?? 'Unknown'
    };
  }
  private getCustomerId(): string{
    return this.CustomerId ?? 'unknown';
  }
  handleError(error: any): void {
    this.ngZone.runOutsideAngular(() => {
      try {
        const errorSource = this.identifyErrorSource(error);
        const navigationPath = this.router.url;
        const modulesInvolved = this.extractModulesFromPath(navigationPath);
        const componentName = this.extractComponentName(error);
        const user = this.getUser();
        const customerId = this.getCustomerId();
        const mechanismType = this.detectMechanismType(error);
        const [baseModule, currentModule] = this.getModuleMarkers(modulesInvolved);
        const event: Sentry.Event = {
          user: {
            id: user?.userId,
            username: user?.userName,
            email: user?.email
          },
          exception: {
            values: [
              {
                type: error.name || 'Error',
                value: error.message || error.toString(),
                mechanism: {
                  handled: false,
                  type: mechanismType
                }
              }
            ]
          },
          tags: {
            errorSource,
            navigationPath,
            componentName,
            customerId: customerId,
            baseModule,
            currentModule

          },
          // Add all modules in extra context
          extra: {
            modulesInvolved,
            routeHierarchy: this.getCurrentRouteHierarchy() // Optional: add nested route info
          },
          level: 'error'
        };
  
        Sentry.captureEvent(event);
        console.error('❌ Global Error Captured:', error, 'Modules:', modulesInvolved);
      } catch (captureError) {
        console.warn('⚠️ Error capturing exception in Sentry:', captureError);
      }
    });
  }
  private getModuleMarkers(modules: string[]): [string, string] {
    return [
      modules[0] || 'AppModule',          // baseModule
      modules[modules.length - 1] || 'AppModule' // currentModule
    ];
  }
  // Optional: Get nested route hierarchy
private getCurrentRouteHierarchy(): string[] {
  let route = this.router.routerState.snapshot.root;
  const hierarchy: string[] = [];
  
  while (route.firstChild) {
    route = route.firstChild;
    if (route.routeConfig?.path) {
      hierarchy.push(route.routeConfig.path);
    }
  }
  
  return hierarchy;
}
  private identifyErrorSource(error: any): string {
    // 1. Browser/DOM Errors
    if (error instanceof DOMException) {
      return `Browser/DOM Error: ${error.name}`; // e.g., "Browser/DOM Error: NotFoundError"
    }
  
    // 2. JavaScript Runtime Errors
    if (error instanceof TypeError) return 'JavaScript Runtime: TypeError';
    if (error instanceof ReferenceError) return 'JavaScript Runtime: ReferenceError';
    if (error instanceof SyntaxError) return 'JavaScript Runtime: SyntaxError';
  
    // 3. Network/HTTP Errors
    if (error?.status) {
      const status = error.status;
      if (status >= 500) return `Server Error (${status})`;
      if (status >= 400) return `Client Error (${status})`;
      return `HTTP Response (${status})`;
    }
  
    // 4. API Response Errors
    if (error?.error?.message) {
      return error.error.code 
        ? `API Error: ${error.error.code}` 
        : 'API Payload Error';
    }
  
    // 5. Angular Specific Errors
    if (error?.ngDebugContext) return 'Angular Template Error';
    if (error.message?.includes('ExpressionChanged')) {
      return 'Angular Change Detection Error';
    }
  
    // 6. Third-party Library Errors
    const thirdPartyMarkers = [
      { pattern: /(google|gapi|maps)/i, name: 'Google API' },
      { pattern: /(stripe|payments)/i, name: 'Stripe' },
      { pattern: /(firebase|firestore)/i, name: 'Firebase' }
    ];
    
    const libMatch = thirdPartyMarkers.find(marker => 
      marker.pattern.test(error.message || error.stack || '')
    );
    if (libMatch) return `Third-party: ${libMatch.name}`;
  
    // 7. Zone.js/Async Errors
    if (error.stack?.includes('zone.js')) {
      return error.message?.includes('unhandled promise rejection')
        ? 'Async: Unhandled Promise Rejection'
        : 'Async: Zone.js Execution';
    }
  
    // 8. Development Mode Errors
    if (!environment.production) {
      if (error.message?.includes('NG0100')) return 'Angular Dev Mode Warning';
    }
  
    // Fallback with intelligent guessing
    if (error.message) {
      if (/timeout/i.test(error.message)) return 'Timeout Error';
      if (/network/i.test(error.message)) return 'Network Failure';
      if (/loading chunk/i.test(error.message)) return 'Code Splitting Error';
    }
  
    return `Unknown (${error.name || 'Error'})`;
  }

  private extractModulesFromPath(path: string): string[] {
    // 1. Clean the path and split into segments
    const segments = path
      .replace(/\?.*$/, '')    // Remove query params
      .replace(/#.*$/, '')     // Remove hash fragments
      .replace(/^\/|\/$/g, '') // Trim slashes
      .split('/')              // Split into path segments
      .filter(segment => segment !== ''); // Remove empty segments
  
    // 2. Convert each segment to module name
    const modules = segments.map(segment => {
      const cleanSegment = segment.replace(/-module$/, ''); // Handle lazy-loading
      
      // Special cases (minimal overrides)
      const SPECIAL_CASES: Record<string, string> = {
        'api': 'ApiModule',
        'auth': 'AuthModule'
        // Add other exceptions as needed
      };
  
      if (SPECIAL_CASES[cleanSegment]) {
        return SPECIAL_CASES[cleanSegment];
      }
  
      // Convert to PascalCaseModule
      return cleanSegment
        .split('-')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join('') + 'Module';
    });
  
    // 3. Ensure at least one module (fallback to AppModule)
    return modules.length > 0 ? modules : ['AppModule'];
  }
  private extractComponentName(error: any): string {
    const stack = error?.stack || '';
  
    // Match all component names like "SomethingComponent"
    const matches = [...stack.matchAll(/at\s+([A-Z][a-zA-Z0-9_]*Component)/g)];
  
    if (!matches || matches.length === 0) return 'Unknown Component';
  
    // List of known irrelevant/global components to exclude
    const excluded = ['AppComponent', 'NgZone', 'Anonymous'];
  
    // Filter out unwanted matches
    const filtered = matches
      .map(m => m[1])
      .filter(name => !excluded.includes(name));
  
    // Return the most probable component (first filtered or first matched)
    return filtered.length > 0 ? filtered[0] : matches[0][1];
  }
  
  private detectMechanismType(error: any): string {
    const message = error?.message?.toLowerCase() || '';
    const stack = error?.stack?.toLowerCase() || '';
  
    // ✅ Most Specific Checks First
    if (stack.includes('zone.js')) return 'zonejs - Angular Zone Error';
    if (stack.includes('core.js') || message.includes('ng') || stack.includes('ngcomponent')) return 'angular - Framework Error';
    if (stack.includes('trycatch') || message.includes('caught') || message.includes('manually handled')) return 'trycatch - Manually Handled';
    if (stack.includes('instrument') || message.includes('intercept')) return 'instrument - Intercepted (e.g. fetch/XHR)';
    if (stack.includes('console') || message.includes('console')) return 'console - Logged via Console';
    if (stack.includes('unhandledrejection') || message.includes('promise')) return 'unhandledrejection - Unhandled Promise';
    if (stack.includes('onerror') || message.includes('window.onerror')) return 'onerror - Global Window Error';
    if (this.router.url) return 'globalhandler - Fallback Angular Route';
  
    return 'generic - Unclassified'; // Least specific
  }
  
  
}
